/**
 * Floating controls for PowerEditor visual editor - undo, redo, apply, discard
 */

import {
  Loader2,
  Undo,
  Redo,
  X,
} from "lucide-react";
import { Hint } from "@/components/ui/hint";
import { PowerEditorFloatingControlsProps } from "@/types/power-editor.types";

export default function PowerEditorFloatingControls({
  pendingChangesCount,
  canUndo,
  canRedo,
  isSavingChanges,
  commitState,
  onUndo,
  onRedo,
  onSaveChanges,
  onDiscardChanges,
}: PowerEditorFloatingControlsProps): JSX.Element | null {
  // Only show controls when there are pending changes
  if (pendingChangesCount === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-1 bg-card/95 backdrop-blur-sm border border-border rounded-lg shadow-lg px-2 py-1.5">
      <Hint
        delayDuration={0}
        text="Undo (Ctrl+Z)"
        side="bottom"
        align="center">
        <button
          onClick={onUndo}
          disabled={!canUndo || isSavingChanges}
          className="p-1.5 rounded hover:bg-accent transition-colors disabled:opacity-30">
          <Undo className="w-4 h-4" />
        </button>
      </Hint>
      <Hint
        delayDuration={0}
        text="Redo (Ctrl+Y)"
        side="bottom"
        align="center">
        <button
          onClick={onRedo}
          disabled={!canRedo || isSavingChanges}
          className="p-1.5 rounded hover:bg-accent transition-colors disabled:opacity-30">
          <Redo className="w-4 h-4" />
        </button>
      </Hint>
      <div className="w-px h-5 bg-border mx-1" />
      <Hint
        delayDuration={0}
        text="Apply visual changes to code"
        side="bottom"
        align="center">
        <button
          onClick={onSaveChanges}
          disabled={isSavingChanges || commitState !== "idle"}
          style={{ backgroundColor: '#625cff' }}
          className="px-3 py-1 text-white text-xs font-medium rounded-md hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5">
          {commitState === "generating" ? (
            <>
              <Loader2 className="animate-spin w-3 h-3" />
              <span>Generating...</span>
            </>
          ) : commitState === "creating" ? (
            <>
              <Loader2 className="animate-spin w-3 h-3" />
              <span>Committing...</span>
            </>
          ) : isSavingChanges ? (
            <>
              <Loader2 className="animate-spin w-3 h-3" />
              <span>Applying...</span>
            </>
          ) : (
            <span>Apply</span>
          )}
        </button>
      </Hint>
      <Hint
        delayDuration={0}
        text="Discard all changes"
        side="bottom"
        align="center">
        <button
          onClick={onDiscardChanges}
          disabled={isSavingChanges}
          className="p-1.5 rounded hover:bg-red-500/10 transition-colors disabled:opacity-50">
          <X className="w-4 h-4 text-muted-foreground hover:text-red-500" />
        </button>
      </Hint>
    </div>
  );
}